import { useEffect, useMemo, useState } from "react";
import { COMMANDS, useRunCommand } from "@/components/CommandPalette";

/**
 * Inline slash menu that appears above the chat input when the user starts a
 * message with "/". Filters by ``query`` (text after the slash).
 */
export default function SlashMenu({ query, onClose }) {
    const run = useRunCommand();
    const [highlight, setHighlight] = useState(0);

    const filtered = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        if (!q) return COMMANDS;
        return COMMANDS.filter(
            (c) => c.key.toLowerCase().slice(1).includes(q) || c.label.toLowerCase().includes(q),
        );
    }, [query]);

    useEffect(() => {
        function onKey(e) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlight((h) => Math.min(h + 1, filtered.length - 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === "Enter" && filtered[highlight]) {
                e.preventDefault();
                onClose();
                run(filtered[highlight].intent);
            } else if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [filtered, highlight, onClose, run]);

    if (filtered.length === 0) return null;

    return (
        <div
            className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-lexiBorder rounded-2xl shadow-lg overflow-hidden"
            data-testid="slash-menu"
        >
            <p className="px-4 pt-2 text-[10px] tracking-[0.22em] uppercase text-inkSoft">
                Commandes rapides
            </p>
            <ul className="max-h-56 overflow-auto py-1">
                {filtered.slice(0, 8).map((c, i) => (
                    <li key={c.key}>
                        <button
                            onMouseEnter={() => setHighlight(i)}
                            onClick={() => {
                                onClose();
                                run(c.intent);
                            }}
                            data-testid={`slash-${c.testid}`}
                            className={`w-full flex items-center gap-3 px-4 py-2 text-left ${
                                i === highlight ? "bg-paperAlt" : "hover:bg-paperAlt"
                            }`}
                        >
                            <c.icon className="w-4 h-4 text-inkSoft" />
                            <span className="font-mono text-xs text-terracotta w-24 shrink-0">
                                {c.key}
                            </span>
                            <span className="text-sm">{c.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}
